import React, { useState } from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import Text from '~/shared/components/Text';
import { RadioButton, mobile } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import { sanitizeReviewLabel } from '../../utils/reviews';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 90%;

  ${mobile`
    width: 100%;
  `}
`;

const StyledRadioBarContainer = styled.div`
  display: flex;
  width: 100%;
  ${({ theme }) => theme.reviewStyles.radioBarContainerStyle};
`;

const TitleContainer = styled.span`
  display: block;
  text-align: left;
  padding-bottom: 15px;
  margin: 0;
  text-transform: initial;
`;

const StyledTitleText = styled(Text).attrs({
  forwardedAs: 'span',
})`
  ${({ isRequired, theme }) =>
    isRequired &&
    css`
      ::after {
        content: '*';
        color: ${theme.colors.textDefault};
      }
    `}
`;

const RatingScaleWrap = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.sizes.xxs}px;
`;

const StyledRatingScaleText = styled(Text).attrs({
  forwardedAs: 'span',
  variant: 'bodyMedEmphasized',
})`
  color: ${({ theme }) => theme.colors.textDeemphasized};
`;

const SelectedRating = styled(StyledRatingScaleText)`
  margin-bottom: ${({ theme }) => theme.sizes.xxs}px;
`;

const inputStyle = (selectedIdx, optionIdx) => {
  const isBefore = optionIdx < selectedIdx;

  return css`
    border: 2px solid
      ${({ theme }) =>
        isBefore ? theme.colors.textEmphasized : theme.colors.palette.black};
    margin: 0;
    ${({ theme }) => theme.reviewStyles.radioBarStyle};
    color: ${({ theme }) =>
      isBefore ? theme.colors.textEmphasized : 'inherit'};
    background: ${({ theme }) =>
      isBefore ? theme.colors.textEmphasized : 'inherit'};
  `;
};

const inputCheckedStyle = css`
  ${({ theme }) => `
    border: 2px solid ${theme.colors.textEmphasized};
    background: ${theme.colors.textEmphasized};
    color: ${theme.colors.textEmphasized};
  `}
`;

const RatingTitle = styled.span`
  padding-right: 4px;
`;

const RatingOption = styled.span`
  color: ${({ theme }) => theme.colors.textDefault};
`;

const DynamicFormattedMessage = FormattedMessage;

const ReviewOptionBar = ({
  children,
  className,
  isRequired,
  label,
  onChange,
  options,
  rbKey,
  title,
}) => {
  const lowestRatingOption = options[0];
  const highestRatingOption = options[options.length - 1];

  const [selectedOptionLabel, setSelectedOptionLabel] = useState({
    selectedOption: 0,
    selectedLabel: '',
  });

  const reviewLabel = sanitizeReviewLabel(label);

  const handleChange = (event) => {
    const answerObjectIdx = options.findIndex(
      (answer) => answer.value === parseInt(event.target.value, 10)
    );

    const answerObject = options[answerObjectIdx];

    const nextState = {
      ...selectedOptionLabel,
      reviewTemplateFieldAnswerId: answerObject.value,
      id: answerObject.reviewTemplateFieldId,
      selectedLabel: answerObject.label,
      selectedOption: parseInt(event.target.value, 10),
      idx: answerObjectIdx,
    };

    setSelectedOptionLabel(nextState);
    if (onChange) {
      onChange(nextState);
    }
  };

  return (
    <Wrapper className={className}>
      <TitleContainer>
        <StyledTitleText variant="formLabelEmphasized" isRequired={isRequired}>
          <DynamicFormattedMessage id={rbKey} defaultMessage={title} />
        </StyledTitleText>
      </TitleContainer>
      <SelectedRating>
        <RatingTitle>
          <DynamicFormattedMessage
            id={`site_account_reviews.review_${reviewLabel}`}
            defaultMessage={label}
          />
        </RatingTitle>
        <RatingOption>{selectedOptionLabel.selectedLabel}</RatingOption>
      </SelectedRating>
      <StyledRadioBarContainer>
        {options.map((option, idx) => (
          <RadioButton
            key={option.label}
            value={option.value}
            id={option.label}
            indexes={[selectedOptionLabel.idx, idx]}
            inputStyle={inputStyle(selectedOptionLabel.idx, idx)}
            inputCheckedStyle={inputCheckedStyle}
            onChange={handleChange}
            rootStyle={{
              flex: 1,
            }}
            checked={selectedOptionLabel.idx === idx}
          />
        ))}
      </StyledRadioBarContainer>
      <RatingScaleWrap>
        <StyledRatingScaleText>
          <DynamicFormattedMessage
            id={`site_account_reviews.review_${reviewLabel}_lowest`}
            defaultMessage={lowestRatingOption.label}
          />
        </StyledRatingScaleText>
        <StyledRatingScaleText>
          <DynamicFormattedMessage
            id={`site_account_reviews.review_${reviewLabel}_highest`}
            defaultMessage={highestRatingOption.label}
          />
        </StyledRatingScaleText>
      </RatingScaleWrap>
      {children}
    </Wrapper>
  );
};

ReviewOptionBar.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  isRequired: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  rbKey: PropTypes.string,
  title: PropTypes.string.isRequired,
};

export default ReviewOptionBar;
